import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Lightbulb, Check, X, PencilSimple, Prohibit, ArrowUpRight } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { Textarea } from "./ui/textarea";
import * as API from "../lib/api";
import { toast } from "sonner";

/**
 * Inline "💡 Save this memory?" chip rendered next to a chat message.
 *
 * Lifecycle:
 *  - Mount → small one-tap chip with content + Save / Edit / Dismiss / Ignore
 *  - Tap Save → POST accept; collapse into a "Saved · View memory →" confirmation
 *  - Tap Edit → expand to inline textarea + Save
 *  - Tap Dismiss → POST dismiss; collapse silently
 *  - Tap Ignore → POST ignore; collapse with notice
 *
 * Failures never affect chat. Once the user resolves the chip we never re-show it.
 */
export default function InlineMemoryChip({ suggestion, onResolved }) {
  const [editing, setEditing] = useState(false);
  const [content, setContent] = useState(suggestion.content);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(null); // { memoryId } when accepted

  const close = () => {
    setTimeout(() => onResolved?.(suggestion.id), 250);
  };

  const accept = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const payload = {};
      if (editing && content.trim() !== suggestion.content) {
        payload.content = content.trim();
      }
      const memory = await API.acceptSuggestion(suggestion.id, payload);
      toast.success("Saved to memory");
      setSaved({ memoryId: memory.id });
    } catch {
      toast.error("Couldn't save");
      setBusy(false);
    }
  };

  const dismiss = async () => {
    if (busy) return;
    setBusy(true);
    API.dismissSuggestion(suggestion.id).catch(() => {});
    close();
  };

  const ignore = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await API.ignoreSuggestion(suggestion.id);
      toast("Won't suggest similar memories again", { duration: 3000 });
    } catch {
      // silent
    }
    close();
  };

  if (saved) {
    return <SavedConfirmation memoryId={saved.memoryId} onResolve={() => onResolved?.(suggestion.id)} suggestionId={suggestion.id} />;
  }

  return (
    <AnimatePresence>
      <motion.div
        layout
        initial={{ opacity: 0, y: -4, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, scale: 0.96, y: -4 }}
        transition={{ duration: 0.22, ease: "easeOut" }}
        data-testid={`inline-suggestion-${suggestion.id}`}
        className="mt-1.5 max-w-[420px] rounded-2xl bg-gradient-to-br from-cyan-500/10 via-cyan-500/5 to-transparent border border-cyan-500/25 px-3 py-2.5"
      >
        <div className="flex items-start gap-2">
          <div className="w-6 h-6 rounded-lg bg-cyan-500/20 border border-cyan-500/30 flex items-center justify-center shrink-0 mt-0.5">
            <Lightbulb size={12} weight="fill" className="text-cyan-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] text-cyan-400/80 font-medium mb-1 flex items-center gap-1.5">
              Save this memory?
              {suggestion.category && (
                <span className="text-white/40 lowercase tracking-normal text-[10px]">
                  · {suggestion.category.replace("_", " ")}
                </span>
              )}
            </div>

            {editing ? (
              <Textarea
                autoFocus
                value={content}
                onChange={(e) => setContent(e.target.value)}
                className="bg-transparent border-white/10 focus-visible:ring-cyan-500/40 min-h-[60px] text-sm mb-2"
                data-testid={`inline-edit-${suggestion.id}`}
              />
            ) : (
              <p className="text-[14px] text-white/90 leading-relaxed">{content}</p>
            )}

            <div className="flex items-center gap-1 mt-2 -mx-1">
              <ActionButton
                onClick={accept}
                disabled={busy || (editing && !content.trim())}
                primary
                testId={`inline-save-${suggestion.id}`}
                icon={Check}
              >
                {editing ? "Save edited" : "Save"}
              </ActionButton>
              {!editing && (
                <ActionButton
                  onClick={() => setEditing(true)}
                  disabled={busy}
                  testId={`inline-edit-button-${suggestion.id}`}
                  icon={PencilSimple}
                >
                  Edit
                </ActionButton>
              )}
              <ActionButton
                onClick={dismiss}
                disabled={busy}
                testId={`inline-dismiss-${suggestion.id}`}
                icon={X}
              >
                Dismiss
              </ActionButton>
              <ActionButton
                onClick={ignore}
                disabled={busy}
                testId={`inline-ignore-${suggestion.id}`}
                icon={Prohibit}
                muted
              >
                Ignore similar
              </ActionButton>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

function SavedConfirmation({ memoryId, suggestionId, onResolve }) {
  useEffect(() => {
    const t = setTimeout(onResolve, 12000);
    return () => clearTimeout(t);
  }, [onResolve]);
  return (
    <AnimatePresence>
      <motion.div
        key="saved"
        layout
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0 }}
        className="mt-1.5 max-w-[420px] rounded-2xl bg-emerald-500/10 border border-emerald-500/25 px-3 py-2"
        data-testid={`inline-saved-${suggestionId}`}
      >
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-lg bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center shrink-0">
            <Check size={11} weight="bold" className="text-emerald-400" />
          </div>
          <span className="text-[13px] text-white/85">Saved to memory</span>
          <Link
            to={`/app/memory?highlight=${memoryId}`}
            data-testid={`inline-jump-${memoryId}`}
            className="ml-auto inline-flex items-center gap-1 text-[12px] text-cyan-300 hover:text-cyan-200 font-medium"
          >
            View memory
            <ArrowUpRight size={12} weight="bold" />
          </Link>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

function ActionButton({ onClick, disabled, primary, muted, icon: Icon, testId, children }) {
  let cls =
    "px-2.5 h-7 inline-flex items-center gap-1 rounded-full text-[12px] font-medium transition-colors disabled:opacity-50";
  if (primary) {
    cls += " bg-cyan-500 text-black hover:bg-cyan-400";
  } else if (muted) {
    cls += " text-white/40 hover:text-white/80";
  } else {
    cls += " text-white/65 hover:text-white hover:bg-white/5";
  }
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={cls}
    >
      {Icon && <Icon size={11} weight="bold" />}
      {children}
    </button>
  );
}
